import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import {
  fetchPolicyDelegations,
  createPolicyDelegation,
  deletePolicyDelegation,
  fetchOrganizationMembers,
  type PolicyDelegationRow,
} from '../../api/ws'
import { useAppStore } from '../../store/app'
import { parseServerTime } from '../../util/serverTime'
import {
  DURATION_LABELS,
  expiryFor,
  scopeToResource,
  describeResource as describeResourcePure,
  type ScopeType,
  type Duration,
} from './delegationScope'

// L1b — the "delegate approval" flow (docs/authz-implementation-plan.md §L1,
// docs/authz-policy-model.md §3.2/§3.4). The homeowner-facing half of L1a.
//
// A delegation lets a housemate approve/decline requests (and tune the rules)
// for something YOU own — the whole household, one agent, or one host — without
// making them a co-owner. Creating one is owner-only (C2, enforced server-side);
// you can hand out authority but a delegate can't hand out more. Revoke anytime.
//
// The resource picker only offers scopes with a resolvable owner; if you aren't
// the owner of what you picked, the server refuses and we show why.

export function DelegationsSheet() {
  const [rows, setRows] = useState<PolicyDelegationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const humanUserID = useAppStore((s) => s.humanUserID)
  const organizationID = useAppStore((s) => s.organizationID)
  const members = useAppStore((s) => s.organizationMembers)
  const agents = useAppStore((s) => s.agentInstances)
  const positions = useAppStore((s) => s.organizationPositions)
  const hosts = useAppStore((s) => s.hosts)

  // Housemates you can delegate to (everyone in the household but you).
  const housemates = useMemo(
    () => members.filter((m) => m.human_user_id !== humanUserID),
    [members, humanUserID],
  )
  // Active agents, presented as "an agent" scope → its position id is what's
  // actually delegated (the tool-call domain is owned per-position).
  const liveAgents = useMemo(
    () => agents.filter((a) => a.status !== 'retired' && !a.retired_at),
    [agents],
  )
  const positionName = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of positions) m.set(p.id, p.name)
    return m
  }, [positions])
  const hostName = useMemo(() => {
    const m = new Map<string, string>()
    for (const h of hosts) m.set(h.host_id, h.hostname)
    return m
  }, [hosts])

  const [delegateID, setDelegateID] = useState('')
  const [scopeType, setScopeType] = useState<ScopeType>('agent')
  const [scopeTargetID, setScopeTargetID] = useState('')
  const [duration, setDuration] = useState<Duration>('permanent')

  async function refresh() {
    setLoading(true)
    try {
      const resp = await fetchPolicyDelegations()
      if (resp.error) setError(resp.error)
      else setError(null)
      setRows(resp.rows ?? [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
    // Housemates aren't kept in the store by default — pull the roster so the
    // delegate picker is populated when the sheet opens.
    if (organizationID) fetchOrganizationMembers(organizationID)
  }, [organizationID])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setNotice(null)
    if (!delegateID) {
      setError('Pick a housemate to delegate to.')
      return
    }
    const scope = scopeToResource(scopeType, scopeTargetID)
    if (!scope) {
      setError('Pick what they can approve.')
      return
    }
    setBusy(true)
    try {
      const resp = await createPolicyDelegation({
        resourceKind: scope.kind,
        resourceID: scope.id,
        delegateHumanID: delegateID,
        expiresAt: expiryFor(duration),
      })
      if (resp.error) {
        setError(resp.error)
        return
      }
      setError(null)
      setNotice('Delegation added.')
      setDelegateID('')
      setScopeTargetID('')
      setDuration('permanent')
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function revoke(row: PolicyDelegationRow) {
    if (!confirm(`Revoke this delegation?\n\n${describeRow(row)}`)) return
    setBusy(true)
    try {
      const resp = await deletePolicyDelegation(row.id)
      if (resp.error) {
        setError(resp.error)
        return
      }
      setError(null)
      setNotice('Delegation revoked.')
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  // Friendly one-liner for a stored delegation's resource.
  function describeResource(row: PolicyDelegationRow): string {
    return describeResourcePure(
      row,
      (id) => positionName.get(id),
      (id) => hostName.get(id),
    )
  }

  function describeRow(row: PolicyDelegationRow): string {
    const who = row.delegate_label || row.delegate_human_user_id
    return `${who} — ${describeResource(row)}`
  }

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      <p style={{ opacity: 0.7, marginTop: 0 }}>
        Let a housemate approve requests and manage rules for something you own —
        the whole household, one agent, or one host. They can act on your behalf,
        but only you (the owner) can grant or revoke this. You can take it back
        anytime.
      </p>

      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="ok">{notice}</Banner>}

      <form
        onSubmit={submit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          margin: '12px 0 20px',
          padding: 12,
          background: 'var(--card, #f7f7f9)',
          borderRadius: 8,
        }}
      >
        <Field label="Delegate to">
          <select value={delegateID} onChange={(e) => setDelegateID(e.target.value)}>
            <option value="">— pick a housemate —</option>
            {housemates.map((m) => (
              <option key={m.human_user_id} value={m.human_user_id}>
                {m.name || m.email || m.human_user_id}
              </option>
            ))}
          </select>
          {housemates.length === 0 && (
            <span style={{ fontSize: 12, color: '#900' }}>
              No other members yet — invite someone first (Households → Invitations).
            </span>
          )}
        </Field>

        <Field label="They can approve">
          <select
            value={scopeType}
            onChange={(e) => {
              setScopeType(e.target.value as ScopeType)
              setScopeTargetID('')
            }}
          >
            <option value="agent">A specific agent</option>
            <option value="host">A specific host</option>
            <option value="household">Everything in this household</option>
          </select>
        </Field>

        {scopeType === 'agent' && (
          <Field label="Which agent">
            <select value={scopeTargetID} onChange={(e) => setScopeTargetID(e.target.value)}>
              <option value="">— pick an agent —</option>
              {liveAgents.map((a) => (
                <option key={a.id} value={a.organization_position_id}>
                  {a.name}
                </option>
              ))}
            </select>
            {liveAgents.length === 0 && (
              <span style={{ fontSize: 12, opacity: 0.6 }}>No active agents.</span>
            )}
          </Field>
        )}

        {scopeType === 'host' && (
          <Field label="Which host">
            <select value={scopeTargetID} onChange={(e) => setScopeTargetID(e.target.value)}>
              <option value="">— pick a host —</option>
              {hosts.map((h) => (
                <option key={h.host_id} value={h.host_id}>
                  {h.hostname}
                </option>
              ))}
            </select>
            {hosts.length === 0 && (
              <span style={{ fontSize: 12, opacity: 0.6 }}>No hosts enrolled.</span>
            )}
          </Field>
        )}

        <Field label="Duration">
          <select value={duration} onChange={(e) => setDuration(e.target.value as Duration)}>
            {(Object.keys(DURATION_LABELS) as Duration[]).map((d) => (
              <option key={d} value={d}>
                {DURATION_LABELS[d]}
              </option>
            ))}
          </select>
        </Field>

        <button
          type="submit"
          disabled={busy || housemates.length === 0}
          style={{
            padding: '10px 12px',
            borderRadius: 6,
            border: 'none',
            background: '#0a84ff',
            color: 'white',
            fontWeight: 600,
            cursor: busy ? 'wait' : 'pointer',
            opacity: housemates.length === 0 ? 0.5 : 1,
          }}
        >
          {busy ? '…' : 'Add delegation'}
        </button>
      </form>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <strong>
          {rows.length} active {rows.length === 1 ? 'delegation' : 'delegations'}
        </strong>
        <button onClick={() => void refresh()} disabled={loading}>
          Refresh
        </button>
      </div>

      {loading ? (
        <p style={{ opacity: 0.6 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ opacity: 0.6 }}>
          Nobody is delegated yet. Everything you own is approved by you alone.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {rows.map((row) => {
            const mine = row.granted_by_human_user_id === humanUserID
            const toMe = row.delegate_human_user_id === humanUserID
            return (
              <li
                key={row.id}
                style={{
                  padding: '12px 0',
                  borderBottom: '1px solid var(--divider, #eee)',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    {row.delegate_label || row.delegate_human_user_id}
                    {toMe && <Tag>you</Tag>}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>
                    {describeResource(row)}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.55, marginTop: 3 }}>
                    granted by {mine ? 'you' : row.granted_by_label || row.granted_by_human_user_id}
                    {row.expires_at ? ` · expires ${formatExpiry(row.expires_at)}` : ' · permanent'}
                  </div>
                </div>
                {/* Owner (grantor) or the delegate themselves may revoke; others
                    get a refusal server-side, so only offer the button when it
                    will work. */}
                {(mine || toMe) && (
                  <button
                    onClick={() => void revoke(row)}
                    disabled={busy}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 6,
                      border: '1px solid var(--divider, #ddd)',
                      background: 'transparent',
                      color: '#c00',
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    {toMe && !mine ? 'Leave' : 'Revoke'}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function formatExpiry(serverTs: string): string {
  // expires_at comes back in SQLite's space-format UTC; parseServerTime reads
  // that (and RFC3339) as UTC. A bare `new Date(...)` would treat it as local.
  const ms = parseServerTime(serverTs)
  if (isNaN(ms)) return serverTs
  return new Date(ms).toLocaleString()
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, opacity: 0.7 }}>{label}</span>
      {children}
    </label>
  )
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        marginLeft: 6,
        fontSize: 11,
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: 8,
        background: '#0a84ff22',
        color: '#0a84ff',
      }}
    >
      {children}
    </span>
  )
}

function Banner({ tone, children }: { tone: 'error' | 'ok'; children: React.ReactNode }) {
  const style: CSSProperties =
    tone === 'error'
      ? { background: '#fff0f0', border: '1px solid #f5a5a5', color: '#900' }
      : { background: '#eefaf0', border: '1px solid #9ad9a8', color: '#0a5' }
  return (
    <div
      role={tone === 'error' ? 'alert' : undefined}
      style={{ ...style, padding: 8, borderRadius: 6, marginBottom: 8, fontSize: 13 }}
    >
      {children}
    </div>
  )
}
